import React, { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import api from "../api/api";
import { NewArrivalProductServices } from "../services/NewArrivalServices";
import ProductCard from "./ProductCard";
import { useLocationPrice } from "../hooks/useLocationPrice";

const SampleNextArrow = memo(({ className, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
    aria-label="Next"
  >
    <i className="ph ph-caret-right" />
  </button>
));

const SamplePrevArrow = memo(({ className, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 hover-border-main-600 text-xl hover-bg-main-600 hover-text-white transition-1`}
    aria-label="Previous"
  >
    <i className="ph ph-caret-left" />
  </button>
));

const getProductImageUrl = (product) => {
  if (!product?.image) return "assets/images/thumbs/NewArrivals/product-img21.png";
  const img = String(product.image).trim();
  if (img.startsWith("http://") || img.startsWith("https://")) return img;
  const backendRoot = (api.defaults.baseURL || "")
    .replace(/\/$/, "")
    .replace(/\/api$/, "");
  if (img.startsWith("storage/")) return `${backendRoot}/${img}`;
  if (img.startsWith("/storage/")) return `${backendRoot}${img}`;
  return `${backendRoot}/storage/${img}`;
};

const NewArrivalOne = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const resolvePrice = useLocationPrice();

  const sliderCards = useMemo(() => {
    return products
      .map((p) => {
        const {
          belongsToLocation,
          newPrice,
          oldPrice,
          discount,
          locationProductPriceId,
        } = resolvePrice(p);

        if (!belongsToLocation) return null;

        return {
          id: p.id,
          imageSrc: getProductImageUrl(p),
          title: p.title,
          category: p.category_name || "Uncategorized",
          oldPrice,
          newPrice,
          discount,
          inStock: true,
          rating: p.avg_rating != null ? Number(p.avg_rating) : null,
          reviewCount: Number(p.review_count ?? 0),
          locationProductPriceId,
        };
      })
      .filter(Boolean);
  }, [products, resolvePrice]);

  const count = sliderCards.length;
  const isSingle = count === 1;

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: count > 1,
      infinite: count > 6,
      speed: 800,
      slidesToShow: Math.min(6, Math.max(1, count)),
      slidesToScroll: 1,
      autoplay: count > 1,
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />,
      responsive: [
        { breakpoint: 1599, settings: { slidesToShow: Math.min(6, Math.max(1, count)) } },
        { breakpoint: 1399, settings: { slidesToShow: Math.min(4, Math.max(1, count)) } },
        { breakpoint: 992,  settings: { slidesToShow: Math.min(3, Math.max(1, count)) } },
        { breakpoint: 575,  settings: { slidesToShow: Math.min(2, Math.max(1, count)) } },
        { breakpoint: 424,  settings: { slidesToShow: 1 } },
      ],
    }),
    [count]
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await NewArrivalProductServices.getNewArrivals(12);
        setProducts(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("New arrivals load error:", e);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (!loading && count === 0) return null;

  return (
    <section className="new-arrival pb-80 pt-80">
      <style>{`
        .new-arrival-heading {
          margin-bottom: 32px;
        }
        .new-arrival-heading h3 {
          font-size: 26px;
          font-weight: 800;
          color: #14532d;
          margin-bottom: 6px;
          line-height: 1.2;
        }
        .new-arrival-heading p {
          color: #3d7a52;
          font-size: 15px;
          margin: 0;
        }
        .new-arrival-view-all {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 700;
          font-size: 14px;
          color: #299e60;
          white-space: nowrap;
          text-decoration: none;
          position: relative;
          transition: color .2s ease;
        }
        .new-arrival-view-all::after {
          content: "";
          position: absolute;
          left: 0; bottom: -3px;
          width: 0%;
          height: 2px;
          background: #299e60;
          transition: width .25s ease;
        }
        .new-arrival-view-all:hover { color: #1d7a49; }
        .new-arrival-view-all:hover::after { width: 100%; }
        .new-arrival-view-all i { transition: transform .25s ease; }
        .new-arrival-view-all:hover i { transform: translateX(4px); }

        .new-arrival-single-wrap {
          display: flex;
          justify-content: center;
          width: 100%;
        }
        .new-arrival-single-item {
          width: 100%;
          max-width: 320px;
        }

        .new-arrival-accent {
          display: inline-block;
          width: 36px;
          height: 4px;
          border-radius: 2px;
          background: linear-gradient(90deg, #299e60, #6dcf9a);
          margin-bottom: 10px;
        }
      `}</style>

      <div className="container container-lg">
        <div className="new-arrival-heading">
          <div className="flex-between flex-wrap gap-8 align-items-end">
            <div>
              <span className="new-arrival-accent" />
              <h3 className="text-heading mb-8">New Arrivals</h3>
              <p className="text-gray-600">Fresh products just added to our store</p>
            </div>
            <Link to="/shop" className="new-arrival-view-all">
              View All Deals <i className="ph ph-arrow-right" />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <p className="text-gray-600 mb-0">Loading new arrivals...</p>
          </div>
        ) : isSingle ? (
          <div className="new-arrival-single-wrap">
            <div className="new-arrival-single-item">
              <ProductCard {...sliderCards[0]} noGrid />
            </div>
          </div>
        ) : (
          <div className="new-arrival__slider arrow-style-two">
            <Slider {...settings}>
              {sliderCards.map((card) => (
                <div key={card.id} className="px-8">
                  <ProductCard {...card} noGrid />
                </div>
              ))}
            </Slider>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewArrivalOne;